import { api } from "@breakout/backend/convex/_generated/api"
import { getExplorerUrl, truncateAddress } from "@breakout/sdk"
import { exhaustiveGuard } from "@breakout/shared/utils"
import { cn } from "@breakout/ui/utils"
import { convexQuery } from "@convex-dev/react-query"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
	AlertTriangle,
	ArrowRight,
	Camera,
	CheckCircle,
	Clock,
	DollarSign,
	FileCheck,
	Pause,
} from "lucide-react"
import { useState } from "react"
import { isAddress, parseUnits } from "viem"
import { useAccount, useWalletClient } from "wagmi"
import { Skeleton } from "../components/skeletons/Skeleton"
import { useExecutionMode } from "../hooks/useExecutionMode"
import { useRegistryConfig } from "../hooks/useRegistryConfig"
import { useAdminConfig } from "../lib/providers"

// =============================================================================
// Constants (module-level to avoid recreation on render)
// =============================================================================

const TIMELINE_STAGES = [
	"pending",
	"active",
	"closed",
	"snapshot",
	"resolved",
	"finalized",
] as const

const STAGE_LABELS = {
	pending: "Pending",
	active: "Active",
	closed: "Closed",
	snapshot: "Snapshot",
	resolved: "Resolved",
	finalized: "Complete",
} as const

const STAGE_COLORS = {
	pending: "bg-gray-500",
	active: "bg-green-500",
	closed: "bg-orange-500",
	snapshot: "bg-yellow-500",
	resolved: "bg-blue-500",
	finalized: "bg-slate-500",
} as const

type ActionType = "needs_snapshot" | "needs_resolution" | "needs_finalization" | "has_fees"

const ACTION_COLORS: Record<ActionType, string> = {
	needs_snapshot: "text-yellow-400 bg-yellow-500/20",
	needs_resolution: "text-blue-400 bg-blue-500/20",
	needs_finalization: "text-gray-400 bg-gray-500/20",
	has_fees: "text-green-400 bg-green-500/20",
}

export const Route = createFileRoute("/")({
	component: Dashboard,
})

function Dashboard() {
	const { isConnected, address } = useAccount()
	const { isHotWalletMode } = useExecutionMode()

	// Fetch pending actions for command center
	// Convex subscriptions are reactive - auto-updates when data changes
	const { data: pendingActions, isLoading: actionsLoading } = useQuery({
		...convexQuery(api.domains.admin.queries.getPendingAdminActions, {}),
		enabled: isConnected,
	})

	// Fetch week timeline
	const { data: timeline, isLoading: timelineLoading } = useQuery({
		...convexQuery(api.domains.admin.queries.getWeekTimeline, {}),
		enabled: isConnected,
	})

	// Fetch weeks for active week link
	const { data: weeks } = useQuery({
		...convexQuery(api.domains.admin.queries.listWeeks, {}),
		enabled: isConnected,
	})

	if (!isConnected) {
		return (
			<div className="flex flex-col items-center justify-center py-20">
				<h1 className="mb-4 text-balance font-bold text-3xl">Breakout Admin</h1>
				<p className="mb-8 text-pretty text-gray-400">
					Connect your wallet to access admin functions.
				</p>
				<div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
					<p className="text-gray-500 text-sm">
						Make sure you connect with a wallet that has resolver or owner permissions.
					</p>
				</div>
			</div>
		)
	}

	const activeWeek = weeks?.find(w => w.status === "active")
	const urgentActions = pendingActions?.actions.filter(a => a.priority === "urgent") ?? []
	const normalActions = pendingActions?.actions.filter(a => a.priority === "normal") ?? []

	return (
		<div className="space-y-8">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="mb-1 font-bold text-2xl">Command Center</h1>
					<p className="text-gray-400">Manage weekly market lifecycle</p>
				</div>
				<div className="flex items-center gap-3">
					<div className="rounded-lg border border-gray-800 bg-gray-900 px-3 py-2">
						<div className="text-gray-500 text-xs">Mode</div>
						<div className="flex items-center gap-1.5">
							<span
								className={cn(
									"inline-block size-2 rounded-full",
									isHotWalletMode ? "bg-green-500" : "bg-blue-500",
								)}
							/>
							<span className="font-medium text-sm">
								{isHotWalletMode ? "Hot Wallet" : "Safe Batch"}
							</span>
						</div>
					</div>
					<div className="rounded-lg border border-gray-800 bg-gray-900 px-3 py-2">
						<div className="text-gray-500 text-xs">Wallet</div>
						<div className="font-mono text-sm">{truncateAddress(address ?? "", 4)}</div>
					</div>
				</div>
			</div>

			{/* Urgent Actions Panel */}
			{urgentActions.length > 0 && (
				<div className="rounded-lg border-2 border-orange-500/50 bg-orange-500/5 p-4">
					<div className="mb-3 flex items-center gap-2">
						<AlertTriangle aria-hidden="true" className="size-5 text-orange-400" />
						<h2 className="font-semibold text-orange-400">Urgent Actions Required</h2>
					</div>
					<div className="space-y-2">
						{urgentActions.map(action => (
							<ActionCard action={action} key={`${action.type}-${action.weekId}`} />
						))}
					</div>
				</div>
			)}

			{/* Normal Actions */}
			{normalActions.length > 0 && (
				<div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
					<h2 className="mb-3 font-semibold text-gray-300">Pending Actions</h2>
					<div className="space-y-2">
						{normalActions.slice(0, 5).map(action => (
							<ActionCard action={action} key={`${action.type}-${action.weekId}`} />
						))}
					</div>
					{normalActions.length > 5 && (
						<p className="mt-3 text-gray-500 text-sm">+{normalActions.length - 5} more actions</p>
					)}
				</div>
			)}

			{/* No Actions State */}
			{!actionsLoading && pendingActions?.actions.length === 0 && (
				<div className="rounded-lg border border-green-500/30 bg-green-500/5 p-6 text-center">
					<CheckCircle aria-hidden="true" className="mx-auto mb-2 size-8 text-green-400" />
					<p className="font-medium text-green-400">All caught up!</p>
					<p className="mt-1 text-gray-500 text-sm">No pending admin actions</p>
				</div>
			)}

			{/* Week Timeline */}
			<div>
				<h2 className="mb-4 font-semibold text-lg">Week Timeline</h2>
				{timelineLoading ? (
					<div className="space-y-3">
						{[0, 1, 2].map(i => (
							<div
								className="flex items-center gap-4 rounded-lg border border-gray-800 bg-gray-900 p-4"
								key={i}
							>
								<div className="w-24">
									<Skeleton className="mb-1 h-4 w-16" />
									<Skeleton className="h-3 w-12" />
								</div>
								<div className="flex flex-1 items-center gap-1">
									{[0, 1, 2, 3, 4, 5].map(j => (
										<Skeleton className="h-2 flex-1 rounded-full" key={j} />
									))}
								</div>
								<Skeleton className="h-6 w-20 rounded-full" />
							</div>
						))}
					</div>
				) : !timeline || timeline.length === 0 ? (
					<div className="rounded-lg border border-gray-800 bg-gray-900 p-6 text-center text-gray-500">
						No weeks found. Create a week to get started.
					</div>
				) : (
					<div className="space-y-3">
						{timeline.slice(0, 5).map(week => (
							<WeekTimelineRow key={week.weekId} week={week} />
						))}
					</div>
				)}
			</div>

			{/* Quick Actions Grid */}
			<div>
				<h2 className="mb-4 font-semibold text-lg">Quick Actions</h2>
				<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
					<Link
						className="group rounded-lg border border-gray-800 bg-gray-900 p-4 transition hover:border-gray-500/50 hover:bg-gray-800"
						to="/launch"
					>
						<div className="mb-2 flex items-center gap-2">
							<div className="rounded-lg bg-gray-500/20 p-2">
								<Camera aria-hidden="true" className="size-4 text-gray-400" />
							</div>
							<span className="font-medium">Launch Week</span>
						</div>
						<p className="text-gray-500 text-sm">Create a new week with 21 markets</p>
					</Link>

					{activeWeek && (
						<Link
							className="group rounded-lg border border-gray-800 bg-gray-900 p-4 transition hover:border-gray-500/50 hover:bg-gray-800"
							params={{ weekId: activeWeek.weekId }}
							to="/weeks/$weekId"
						>
							<div className="mb-2 flex items-center gap-2">
								<div className="rounded-lg bg-green-500/20 p-2">
									<Clock aria-hidden="true" className="size-4 text-green-400" />
								</div>
								<span className="font-medium">Active Week</span>
							</div>
							<p className="text-gray-500 text-sm">
								{activeWeek.weekId.slice(0, 8)}... ({activeWeek.marketCount} markets)
							</p>
						</Link>
					)}

					<Link
						className="group rounded-lg border border-gray-800 bg-gray-900 p-4 transition hover:border-red-500/50 hover:bg-gray-800"
						to="/emergency"
					>
						<div className="mb-2 flex items-center gap-2">
							<div className="rounded-lg bg-red-500/20 p-2">
								<Pause aria-hidden="true" className="size-4 text-red-400" />
							</div>
							<span className="font-medium">Emergency</span>
						</div>
						<p className="text-gray-500 text-sm">Pause/unpause markets if needed</p>
					</Link>
				</div>
			</div>

			{/* Contract Addresses */}
			<ContractAddressesTable />

			{/* USDC Mint (Testnet only) */}
			<UsdcMintSection />
		</div>
	)
}

interface Action {
	type: ActionType
	weekId: string
	priority: "urgent" | "normal"
	message: string
	hoursSince?: number
	marketCount: number
	metadata?: {
		totalFees?: number
		resolvedCount?: number
		finalizedCount?: number
	}
}

function ActionIcon({ type }: { type: ActionType }) {
	switch (type) {
		case "needs_snapshot":
			return <Camera aria-hidden="true" className="size-4" />
		case "needs_resolution":
			return <FileCheck aria-hidden="true" className="size-4" />
		case "needs_finalization":
			return <CheckCircle aria-hidden="true" className="size-4" />
		case "has_fees":
			return <DollarSign aria-hidden="true" className="size-4" />
		default:
			return exhaustiveGuard(type)
	}
}

function ActionCard({ action }: { action: Action }) {
	// Use typed route with params for proper TanStack Router navigation
	const searchParams = action.type === "has_fees" ? { tab: "fees" as const } : undefined

	return (
		<Link
			className="flex items-center justify-between rounded-lg border border-gray-800 bg-gray-900/50 p-3 transition hover:border-gray-700 hover:bg-gray-800/50"
			params={{ weekId: action.weekId }}
			search={searchParams}
			to="/weeks/$weekId"
		>
			<div className="flex items-center gap-3">
				<div className={`rounded-lg p-2 ${ACTION_COLORS[action.type]}`}>
					<ActionIcon type={action.type} />
				</div>
				<div>
					<div className="text-sm">{action.message}</div>
					<div className="font-mono text-gray-500 text-xs">
						{action.weekId.slice(0, 8)}... • {action.marketCount} markets
					</div>
				</div>
			</div>
			<ArrowRight aria-hidden="true" className="size-4 text-gray-500" />
		</Link>
	)
}

interface TimelineWeek {
	weekId: string
	stage: "pending" | "active" | "closed" | "snapshot" | "resolved" | "finalized"
	stageIndex: number
	marketCount: number
	resolvedCount: number
	hasEndSnapshot: boolean
	openTimeSec: number
	cutoffTimeSec: number
}

function WeekTimelineRow({ week }: { week: TimelineWeek }) {
	return (
		<Link
			className="flex items-center gap-4 rounded-lg border border-gray-800 bg-gray-900 p-4 transition hover:border-gray-700"
			params={{ weekId: week.weekId }}
			to="/weeks/$weekId"
		>
			<div className="w-24">
				<div className="font-mono text-sm">{week.weekId.slice(0, 8)}</div>
				<div className="text-gray-500 text-xs">{week.marketCount} markets</div>
			</div>

			{/* Timeline Progress */}
			<div className="flex flex-1 items-center gap-1">
				{TIMELINE_STAGES.map((stage, i) => (
					<div className="flex flex-1 items-center" key={stage}>
						<div
							className={cn(
								"h-2 flex-1 rounded-full",
								i <= week.stageIndex ? STAGE_COLORS[week.stage] : "bg-gray-700",
							)}
						/>
						{i < TIMELINE_STAGES.length - 1 && <div className="h-0.5 w-1 bg-gray-700" />}
					</div>
				))}
			</div>

			{/* Current Stage Badge */}
			<div
				className={cn(
					"rounded-full bg-opacity-20 px-3 py-1 text-white text-xs",
					STAGE_COLORS[week.stage],
				)}
			>
				{STAGE_LABELS[week.stage]}
			</div>
		</Link>
	)
}

function ContractAddressesTable() {
	const { chainConfig, chainId } = useAdminConfig()
	const { operator, resolver, treasury, isLoading: registryLoading } = useRegistryConfig()

	// Static contracts (from config - immutable)
	const staticContracts = [
		{ label: "Factory", address: chainConfig.factory },
		{ label: "Implementation", address: chainConfig.implementation },
		{ label: "Registry", address: chainConfig.registry },
		{ label: "USDC", address: chainConfig.usdc },
	]

	// Dynamic contracts (from registry on-chain - can change)
	const dynamicContracts = [
		{ label: "Operator", address: operator },
		{ label: "Resolver", address: resolver },
		{ label: "Treasury", address: treasury },
	]

	return (
		<div>
			<h2 className="mb-4 font-semibold text-lg">Contract Addresses</h2>
			<div className="overflow-x-auto rounded-lg border border-gray-800 bg-gray-900">
				<table className="w-full text-left text-sm">
					<thead>
						<tr className="border-gray-800 border-b text-gray-500">
							<th className="px-4 py-3">Contract</th>
							<th className="px-4 py-3">Address</th>
							<th className="px-4 py-3">Source</th>
							<th className="px-4 py-3">Explorer</th>
						</tr>
					</thead>
					<tbody>
						{staticContracts.map(({ label, address }) => (
							<tr className="border-gray-800 border-b" key={label}>
								<td className="px-4 py-3 font-medium">{label}</td>
								<td className="px-4 py-3 font-mono text-gray-400 text-sm">
									{truncateAddress(address, 6)}
								</td>
								<td className="px-4 py-3">
									<span className="rounded bg-gray-700 px-1.5 py-0.5 text-gray-400 text-xs">
										config
									</span>
								</td>
								<td className="px-4 py-3">
									<a
										className="text-gray-400 hover:text-gray-300"
										href={getExplorerUrl(chainId, address)}
										rel="noopener noreferrer"
										target="_blank"
									>
										View
									</a>
								</td>
							</tr>
						))}
						{dynamicContracts.map(({ label, address }) => (
							<tr className="border-gray-800 border-b last:border-0" key={label}>
								<td className="px-4 py-3 font-medium">{label}</td>
								<td className="px-4 py-3 font-mono text-gray-400 text-sm">
									{registryLoading ? (
										<span className="inline-block h-4 w-24 animate-pulse rounded bg-gray-700" />
									) : address ? (
										truncateAddress(address, 6)
									) : (
										<span className="text-gray-600">-</span>
									)}
								</td>
								<td className="px-4 py-3">
									<span className="rounded bg-green-900/50 px-1.5 py-0.5 text-green-400 text-xs">
										on-chain
									</span>
								</td>
								<td className="px-4 py-3">
									{address ? (
										<a
											className="text-gray-400 hover:text-gray-300"
											href={getExplorerUrl(chainId, address)}
											rel="noopener noreferrer"
											target="_blank"
										>
											View
										</a>
									) : (
										<span className="text-gray-600">-</span>
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<p className="mt-2 text-gray-500 text-xs">
				Chain: {chainConfig.name} ({chainId})
			</p>
		</div>
	)
}

function UsdcMintSection() {
	const { chainConfig } = useAdminConfig()
	const { data: walletClient } = useWalletClient()
	const { address } = useAccount()
	const [recipient, setRecipient] = useState("")
	const [amount, setAmount] = useState("")
	const [isMinting, setIsMinting] = useState(false)
	const [mintStatus, setMintStatus] = useState<{ success: boolean; message: string } | null>(null)

	// Only show on testnet
	if (!chainConfig.isTestnet) {
		return null
	}

	const handleMint = async () => {
		if (!walletClient || !address) {
			setMintStatus({ success: false, message: "Connect wallet first" })
			return
		}

		const targetRaw = recipient.trim() || address
		if (!isAddress(targetRaw)) {
			setMintStatus({ success: false, message: "Invalid recipient address" })
			return
		}
		const targetAddress = targetRaw
		const amountUsdc = parseUnits(amount || "1000", 6)

		setIsMinting(true)
		setMintStatus(null)

		try {
			const hash = await walletClient.writeContract({
				address: chainConfig.usdc,
				abi: [
					{
						name: "mint",
						type: "function",
						stateMutability: "nonpayable",
						inputs: [
							{ name: "to", type: "address" },
							{ name: "amount", type: "uint256" },
						],
						outputs: [],
					},
				],
				functionName: "mint",
				args: [targetAddress, amountUsdc],
			})
			setMintStatus({
				success: true,
				message: `Minted ${amount || "1000"} USDC. Tx: ${hash.slice(0, 10)}...`,
			})
			setRecipient("")
			setAmount("")
		} catch (err) {
			setMintStatus({
				success: false,
				message: err instanceof Error ? err.message : "Mint failed",
			})
		} finally {
			setIsMinting(false)
		}
	}

	return (
		<div>
			<h2 className="mb-4 font-semibold text-lg">Mint Test USDC</h2>
			<div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
				<div className="grid gap-4 sm:grid-cols-3">
					<div>
						<label className="mb-1 block text-gray-500 text-sm" htmlFor="recipient">
							Recipient (empty = self)
						</label>
						<input
							className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 font-mono text-sm focus:border-gray-500 focus:outline-none"
							id="recipient"
							onChange={e => setRecipient(e.target.value)}
							placeholder={address ? truncateAddress(address, 6) : "0x..."}
							type="text"
							value={recipient}
						/>
					</div>
					<div>
						<label className="mb-1 block text-gray-500 text-sm" htmlFor="amount">
							Amount (USDC)
						</label>
						<input
							className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none"
							id="amount"
							onChange={e => setAmount(e.target.value)}
							placeholder="1000"
							type="text"
							value={amount}
						/>
					</div>
					<div className="flex items-end">
						<button
							aria-busy={isMinting}
							className="w-full rounded bg-gray-600 px-4 py-2 font-medium text-sm hover:bg-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
							disabled={isMinting}
							onClick={handleMint}
							type="button"
						>
							{isMinting ? "Minting..." : "Mint USDC"}
						</button>
					</div>
				</div>
				{mintStatus && (
					<div
						className={cn(
							"mt-3 rounded p-2 text-sm",
							mintStatus.success ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400",
						)}
					>
						{mintStatus.message}
					</div>
				)}
				<p className="mt-3 text-gray-500 text-xs">
					Testnet only. Uses the mock USDC contract's public mint function.
				</p>
			</div>
		</div>
	)
}
